import React, { useState } from 'react';
import styles from './styles.module.scss';
import Flex from 'src/components/Flex';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon';
import LoadingIcon from 'src/components/LoadingIcon';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { SurveyMigrationPreview } from 'src/api/surveyMigration';
import { useStartSurveyMigration } from 'src/queries/useSurveyMigration';

const STATE_LABELS: Record<string, string> = {
    SURVEY_CREATED: 'Created',
    SURVEY_OPENED: 'Opened',
    SURVEY_STARTED: 'In Progress',
    SURVEY_COMPLETED: 'Completed',
    SURVEY_CANCELLED: 'Cancelled',
    CONTACT_INITIATED: 'Contact Initiated',
};

interface PreviewSectionProps {
    preview: SurveyMigrationPreview | null | undefined;
    isLoading: boolean;
    error: unknown;
    jobInProgress: boolean;
}

function stateLabel(code: string): string {
    return STATE_LABELS[code] ?? code;
}

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
}

function PreviewSection({ preview, isLoading, error, jobInProgress }: PreviewSectionProps) {
    const startMigration = useStartSurveyMigration();
    const [showConfirm, setShowConfirm] = useState(false);

    const handleStart = () => {
        if (jobInProgress || startMigration.isLoading) {
            return;
        }
        setShowConfirm(true);
    };

    const handleConfirm = () => {
        setShowConfirm(false);
        if (jobInProgress || startMigration.isLoading) {
            return;
        }
        startMigration.mutate();
    };

    if (isLoading) {
        return (
            <Card className={styles.cardWrap} label="Preview">
                <Flex justify="center" className={styles.sectionBody}>
                    <LoadingIcon />
                </Flex>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className={styles.cardWrap} label="Preview">
                <div className={styles.error}>{errorMessage(error, 'Failed to load migration preview.')}</div>
            </Card>
        );
    }

    if (!preview) {
        return null;
    }

    return (
        <Card className={styles.cardWrap} label="Preview">
            <Flex direction="column" className={styles.sectionBody}>
                <table className={styles.previewTable}>
                    <thead>
                        <tr>
                            <th>Survey State</th>
                            <th className={styles.numberCol}>Count</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(preview.stateCounts).map(([code, count]) => (
                            <tr key={code}>
                                <td>{stateLabel(code)}</td>
                                <td className={styles.numberCol}>{count.toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total Surveys</td>
                            <td className={styles.numberCol}>{preview.totalSurveys.toLocaleString()}</td>
                        </tr>
                    </tfoot>
                </table>

                <Flex direction="column" className={styles.previewMeta}>
                    <div>
                        <span className={styles.metaLabel}>Date range:</span> {preview.dateRange.earliest} —{' '}
                        {preview.dateRange.latest}
                    </div>
                    <div>
                        <span className={styles.metaLabel}>Facilities:</span> {preview.facilityCount}
                    </div>
                </Flex>

                <Flex direction="row" align="center" className={styles.warningCallout}>
                    <Icon type="fa" name="exclamation-triangle" className={styles.warningIcon} />
                    <span>
                        Survey sending will be paused during migration. New surveys will resume once migration
                        completes.
                    </span>
                </Flex>

                {startMigration.isError && (
                    <div className={styles.error}>{errorMessage(startMigration.error, 'Failed to start migration.')}</div>
                )}

                <Flex direction="row" className={styles.buttonRow}>
                    <Button
                        type="primary"
                        text="Start Migration"
                        isLoading={startMigration.isLoading}
                        disabled={jobInProgress || startMigration.isLoading}
                        onClick={handleStart}
                    />
                </Flex>

                {showConfirm && (
                    <ConfirmationModal
                        headerTitle="Start Migration"
                        title={`This will migrate ${preview.totalSurveys.toLocaleString()} surveys. Survey sending will be paused until migration completes.`}
                        confirmButtonText="Start Migration"
                        cancelButtonText="Cancel"
                        onConfirm={handleConfirm}
                        onCancel={() => setShowConfirm(false)}
                        isLoading={startMigration.isLoading}
                    />
                )}
            </Flex>
        </Card>
    );
}

export default PreviewSection;
